import React from "react";
import {useLocale} from "i18n";
import {ContentSurface, HorizontalTableRegion, Prose, WideComparisonTable} from "components/ContentPage";
import {zeroKnowledgeCopy} from "./copy/zeroKnowledge";

/**
 * Public, crawlable **Zero-Knowledge / E2EE** explainer page body (`/zero-knowledge`, ui#133).
 *
 * Dumb view: all copy comes from {@link ./copy/zeroKnowledge.ts} (both locales), selected at runtime
 * by `useLocale()`, so the `/ro` SSG tree renders Romanian for free. SSG-safe: pure token-styled
 * markup (semantic HTML inside {@link Prose}), no browser APIs, no portals. The two-mode comparison
 * is a real `<table>` (crawlable, `scope`-annotated) wrapped in a horizontal scroller so a phone
 * never scrolls the page sideways.
 */
export function ZeroKnowledgeContent() {
	const {locale} = useLocale();
	const copy = zeroKnowledgeCopy[locale];
	return (
		<ContentSurface>
			<Prose>
				<h1>{copy.heading}</h1>
				<ZeroKnowledgeBody/>
			</Prose>
		</ContentSurface>
	);
}

/** Route-neutral Zero-Knowledge copy shared by the in-flow page and the Resources dialog. */
export function ZeroKnowledgeBody() {
	const {locale} = useLocale();
	const copy = zeroKnowledgeCopy[locale];
	const {comparison} = copy;
	return (
		<>
				<p>{copy.lede}</p>
				{copy.intro.map((paragraph, index) => (
					<p key={index}>{paragraph}</p>
				))}

				<h2>{comparison.heading}</h2>
				<p>{comparison.intro}</p>
				<HorizontalTableRegion role="region" tabIndex={0} aria-label={comparison.heading}>
					<WideComparisonTable>
						<thead>
							<tr>
								<th scope="col">{comparison.featureHeading}</th>
								<th scope="col">{comparison.escrowHeading}</th>
								<th scope="col">{comparison.zeroKnowledgeHeading}</th>
							</tr>
						</thead>
						<tbody>
							{comparison.rows.map((row) => (
								<tr key={row.label}>
									<th scope="row">{row.label}</th>
									<td>{row.escrow}</td>
									<td>{row.zeroKnowledge}</td>
								</tr>
							))}
						</tbody>
					</WideComparisonTable>
				</HorizontalTableRegion>

				{copy.sections.map((section) => (
					<React.Fragment key={section.heading}>
						<h2>{section.heading}</h2>
						{section.paragraphs.map((paragraph, index) => (
							<p key={index}>{paragraph}</p>
						))}
						{section.bullets && (
							<ul>
								{section.bullets.map((bullet) => (
									<li key={bullet}>{bullet}</li>
								))}
							</ul>
						)}
						{section.closing && <p>{section.closing}</p>}
					</React.Fragment>
				))}

				<p>{copy.closing}</p>
		</>
	);
}
